import {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  memo,
  useRef,
} from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { MaterialContext } from "../context/MaterialContext";

import navBarStyle from "../styles/nav.module.scss";
import FilterBox from "./FilterBox";
import UserBox from "./UserBox";
import {
  downArrowIcon,
  designerIcon,
  vendorIcon,
  guestIcon,
} from "./Icons";
import SearchBar from "./SearchBar";
import MobileChromeBar from "./mobile/MobileChromeBar";
import { getDisplayUserType } from "../utilities/loginAccount";

function NavBar() {
  const { collection, userInformation, changelogOutUser, media } =
    useContext(UserContext);
  const { isFilter } = useContext(MaterialContext);
  const [userMenuIsVisible, setUserMenuIsVisible] = useState(false);
  const [filterMenuIsVisible, setFilterMenuIsVisible] = useState(false);
  const [mobileNavHidden, setMobileNavHidden] = useState(false);
  const lastScrollYRef = useRef(0);
  const scrollTickingRef = useRef(false);
  const scrollRafRef = useRef(null);
  const userMenuButtonRef = useRef(null);
  const userBoxRef = useRef(null);
  const filterButtonRef = useRef(null);
  const filterBoxRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isLibraryRoute = location.pathname === "/library";
  const isSingleMaterialRoute =
    /^\/[^/]+\/[^/]+$/.test(location.pathname) &&
    !location.pathname.startsWith("/material/");
  const isImmersiveMobileRoute =
    location.pathname === "/help" || isSingleMaterialRoute;
  const collectionCount = collection?.length || 0;

  const displayUserType = getDisplayUserType(userInformation);
  const activeUserIcon = useMemo(() => {
    if (displayUserType === "vendor" || displayUserType === "vendoradmin") {
      return vendorIcon;
    }
    if (displayUserType === "designer") {
      return designerIcon;
    }
    return guestIcon;
  }, [displayUserType]);

  useEffect(() => {
    if (location.pathname !== "/library") {
      setUserMenuIsVisible(false);
      setFilterMenuIsVisible(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!userMenuIsVisible && !filterMenuIsVisible) {
      return;
    }

    const handlePointerDown = (event) => {
      const target = event.target;

      if (userMenuIsVisible) {
        if (
          !userMenuButtonRef.current?.contains(target) &&
          !userBoxRef.current?.contains(target)
        ) {
          setUserMenuIsVisible(false);
        }
      }

      if (filterMenuIsVisible) {
        if (
          !filterButtonRef.current?.contains(target) &&
          !filterBoxRef.current?.contains(target)
        ) {
          setFilterMenuIsVisible(false);
        }
      }
    };

    const handleEscape = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      setUserMenuIsVisible(false);
      setFilterMenuIsVisible(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [userMenuIsVisible, filterMenuIsVisible]);

  useEffect(() => {
    if (media !== "mobile") {
      document.body.style.overflow = "";
      setMobileNavHidden(false);
      return;
    }

    if (isImmersiveMobileRoute) {
      setMobileNavHidden(false);
      return;
    }

    lastScrollYRef.current = window.scrollY;

    const updateNavOnScroll = () => {
      const currentScrollY = window.scrollY;

      if (userMenuIsVisible || filterMenuIsVisible) {
        setMobileNavHidden(false);
        lastScrollYRef.current = currentScrollY;
        scrollTickingRef.current = false;
        return;
      }

      if (currentScrollY <= 30) {
        setMobileNavHidden(false);
        lastScrollYRef.current = currentScrollY;
        scrollTickingRef.current = false;
        return;
      }

      const scrollDelta = currentScrollY - lastScrollYRef.current;

      if (scrollDelta > 8) {
        setMobileNavHidden((prev) => (prev ? prev : true));
      } else if (scrollDelta < -8) {
        setMobileNavHidden((prev) => (prev ? false : prev));
      }

      lastScrollYRef.current = currentScrollY;
      scrollTickingRef.current = false;
    };

    const handleScroll = () => {
      if (scrollTickingRef.current) {
        return;
      }

      scrollTickingRef.current = true;
      scrollRafRef.current = window.requestAnimationFrame(updateNavOnScroll);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
      scrollTickingRef.current = false;
    };
  }, [
    media,
    isImmersiveMobileRoute,
    userMenuIsVisible,
    filterMenuIsVisible,
  ]);

  const handleClickAction = useCallback(
    (navigateTo) => {
      navigate(navigateTo);
    },
    [navigate],
  );

  const handleDropDownMenu = useCallback((type) => {
    if (type === "user") {
      setUserMenuIsVisible((prev) => !prev);
      setFilterMenuIsVisible(false);
      return;
    }

    if (type === "filter") {
      setUserMenuIsVisible(false);
      setFilterMenuIsVisible((prev) => !prev);
    }
  }, []);

  const handleMobileClose = useCallback(() => {
    if (location.key && location.key !== "default") {
      navigate(-1);
      return;
    }

    navigate("/library");
  }, [location.key, navigate]);

  const handleShowFilterMenu = useCallback(() => {
    handleDropDownMenu("filter");
  }, [handleDropDownMenu]);

  const handleShowUserMenu = useCallback(() => {
    handleDropDownMenu("user");
  }, [handleDropDownMenu]);

  return (
    <>
      <FilterBox
        ref={filterBoxRef}
        filterMenuIsVisible={filterMenuIsVisible}
        setFilterMenuIsVisible={setFilterMenuIsVisible}
      />
      <UserBox
        ref={userBoxRef}
        userInformation={userInformation}
        changelogOutUser={changelogOutUser}
        userMenuIsVisible={userMenuIsVisible}
        setUserMenuIsVisible={setUserMenuIsVisible}
        collectionCount={collectionCount}
        includeCollectionLink={media === "mobile"}
      />

      {media === "mobile" ? (
        <MobileNav
          isImmersiveMobileRoute={isImmersiveMobileRoute}
          mobileNavHidden={mobileNavHidden}
          activeUserIcon={activeUserIcon}
          isLibraryRoute={isLibraryRoute}
          isFilter={isFilter}
          filterMenuIsVisible={filterMenuIsVisible}
          userMenuIsVisible={userMenuIsVisible}
          userMenuButtonRef={userMenuButtonRef}
          filterButtonRef={filterButtonRef}
          onClose={handleMobileClose}
          onShowFilter={handleShowFilterMenu}
          onShowUser={handleShowUserMenu}
          onLogoClick={handleClickAction}
        />
      ) : (
        <DesktopNav
          isLibraryRoute={isLibraryRoute}
          isFilter={isFilter}
          filterMenuIsVisible={filterMenuIsVisible}
          userMenuIsVisible={userMenuIsVisible}
          collectionCount={collectionCount}
          activeUserIcon={activeUserIcon}
          userMenuButtonRef={userMenuButtonRef}
          filterButtonRef={filterButtonRef}
          onShowFilter={handleShowFilterMenu}
          onShowUser={handleShowUserMenu}
          onLogoClick={handleClickAction}
        />
      )}
    </>
  );
}

const MobileNav = memo(function MobileNav({
  isImmersiveMobileRoute,
  mobileNavHidden,
  activeUserIcon,
  isLibraryRoute,
  isFilter,
  filterMenuIsVisible,
  userMenuIsVisible,
  userMenuButtonRef,
  filterButtonRef,
  onClose,
  onShowFilter,
  onShowUser,
  onLogoClick,
}) {
  if (isImmersiveMobileRoute) {
    return (
      <MobileChromeBar
        fixed
        onLogoClick={() => onLogoClick("/library")}
        onClose={onClose}
      />
    );
  }

  return (
    <div
      className={`${navBarStyle.bar} ${navBarStyle.mobileBar} ${
        isLibraryRoute ? navBarStyle.mobileBarWithFilter : ""
      } ${mobileNavHidden ? navBarStyle.mobileBarHidden : ""}`}
    >
      <div
        onClick={() => onLogoClick("/library")}
        className={navBarStyle.logo}
      >
        MATLIB
      </div>
      <SearchBar />
      <div className={navBarStyle.navigation}>
        {isLibraryRoute ? (
          <button
            type="button"
            ref={filterButtonRef}
            className={`${navBarStyle.filter} ${
              filterMenuIsVisible ? navBarStyle.active : ""
            }`}
            onClick={onShowFilter}
            aria-expanded={filterMenuIsVisible}
            aria-haspopup="dialog"
          >
            {isFilter ? <span>Filtered</span> : <span>Filter</span>}
          </button>
        ) : null}
        <button
          type="button"
          ref={userMenuButtonRef}
          className={`${navBarStyle.userMenu} ${
            userMenuIsVisible ? navBarStyle.active : ""
          }`}
          onClick={onShowUser}
          aria-expanded={userMenuIsVisible}
          aria-haspopup="menu"
          aria-label="Account menu"
        >
          {activeUserIcon}
        </button>
      </div>
    </div>
  );
});

const DesktopNav = memo(function DesktopNav({
  isLibraryRoute,
  isFilter,
  filterMenuIsVisible,
  userMenuIsVisible,
  collectionCount,
  activeUserIcon,
  userMenuButtonRef,
  filterButtonRef,
  onShowFilter,
  onShowUser,
  onLogoClick,
}) {
  return (
    <div className={navBarStyle.bar}>
      <div onClick={() => onLogoClick("/library")} className={navBarStyle.logo}>
        MATLIB
      </div>
      <SearchBar />
      <div className={navBarStyle.navigation}>
        {isLibraryRoute ? (
          <button
            type="button"
            ref={filterButtonRef}
            className={`${navBarStyle.filter} ${
              filterMenuIsVisible ? navBarStyle.active : ""
            }`}
            onClick={onShowFilter}
            aria-expanded={filterMenuIsVisible}
            aria-haspopup="dialog"
          >
            {isFilter ? <span>Filtered</span> : <span>Filter</span>}
            {downArrowIcon}
          </button>
        ) : null}
        <NavLink
          className={({ isActive }) => (isActive ? "active" : "inactive")}
          to="library"
        >
          LIBRARY
        </NavLink>
        <NavLink
          className={({ isActive }) =>
            `${isActive ? "active" : "inactive"} ${navBarStyle.collectionLink}`
          }
          to="collection"
          aria-label={`Collection, ${collectionCount} saved`}
        >
          <span className={navBarStyle.collectionLabel}>COLLECTION</span>
          <span className={navBarStyle.collectionCount}>{collectionCount}</span>
        </NavLink>
        <button
          type="button"
          ref={userMenuButtonRef}
          className={`${navBarStyle.userMenu} ${
            userMenuIsVisible ? navBarStyle.active : ""
          }`}
          onClick={onShowUser}
          aria-expanded={userMenuIsVisible}
          aria-haspopup="menu"
          aria-label="Account menu"
        >
          {activeUserIcon}
        </button>
      </div>
    </div>
  );
});

export default NavBar;
